import { format, subDays, subYears } from 'date-fns'
import { parseCsvParam, serializeCsvParam } from './csv-search-param'
import {
  formatDate,
  formatDateOnlyShort,
  formatDateTime,
  formatDurationMs,
} from './formatters'
import type {
  AvomaSourceStatus,
  MeetingCallCategory,
  MeetingHubspotCandidate,
  MeetingHubspotContactRef,
  MeetingHubspotLinkReview,
  MeetingHubspotLinkState,
  MeetingLinkEvidenceKind,
  MeetingLinkStatus,
  MeetingRecordingDetail,
  MeetingRecordingRow,
  MeetingSourceSyncState,
  MeetingSuggestionReason,
  MeetingSyncBatchStatus,
  MeetingSyncBatchView,
  MeetingTranscriptCounts,
  MeetingTranscriptCueView,
  MeetingTranscriptSource,
  MeetingTranscriptStatus,
  MeetingTranscriptSummary,
  MeetingTranscriptsStatus,
  MeetingTypeOption,
  MeetingsFilterState,
  ZoomSourceStatus,
  ZoomWebhookEventStatus,
} from '@/types/meetingTranscripts.types'
import {
  MEETING_CALL_CATEGORIES,
  MEETING_HUBSPOT_LINK_STATES,
  MEETING_LINK_STATUSES,
  MEETING_TRANSCRIPT_FILTERS,
  MEETING_TRANSCRIPT_SOURCES,
} from '@/types/meetingTranscripts.types'

/**
 * sc-863. Pure helpers behind the backoffice Meeting transcripts page: source,
 * status labels and badge tones, the hub-card status, replay emphasis, offset
 * and duration formatting, the backfill defaults, and the transcript grouping
 * and search used by the viewer. No React.
 */

export interface StatusBadgeConfig {
  label: string
  className: string
}

/** The badge tones the page paints with; a call tag's `color` names one of them. */
export const TONE = {
  success:
    'border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400',
  warning:
    'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  info: 'border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-400',
  danger: 'border-destructive/30 bg-destructive/10 text-destructive',
  violet:
    'border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-400',
  neutral: 'border-muted-foreground/30 bg-muted/30 text-muted-foreground',
} as const

/** The two places a recorded meeting can come from. */
export interface SourceConfig extends StatusBadgeConfig {
  /** The brand's domain, for the logo.dev tile. */
  brandDomain: string
}

export const SOURCE_CONFIG: Record<MeetingTranscriptSource, SourceConfig> = {
  ZOOM: { label: 'Zoom', brandDomain: 'zoom.us', className: TONE.info },
  AVOMA: { label: 'Avoma', brandDomain: 'avoma.com', className: TONE.violet },
}

/** `?source=` as the source it names, or `undefined` for anything else. */
export function parseSourceParam(
  raw: string | undefined | null,
): MeetingTranscriptSource | undefined {
  return MEETING_TRANSCRIPT_SOURCES.find((value) => value === raw)
}

/**
 * The sources that recorded a call, in the order the page always lists them
 * (Zoom, then Avoma): the row's own and its copies'. A list row is ONE call, so
 * its Source column shows one badge per source that recorded it.
 */
export function callSources(
  call: Pick<MeetingRecordingRow, 'source' | 'copies'>,
): Array<MeetingTranscriptSource> {
  const recorded = new Set<MeetingTranscriptSource>([
    call.source,
    ...call.copies.map((copy) => copy.source),
  ])
  return MEETING_TRANSCRIPT_SOURCES.filter((source) => recorded.has(source))
}

/**
 * The line under the HubSpot meeting section of a call both sources recorded: a
 * review (attach, choose another, not a booked meeting) applies to every
 * recording of the call. `null` for a call only one source recorded.
 *
 *   "Applies to the Zoom and Avoma recordings of this call."
 */
export function appliesToRecordingsNote(
  call: Pick<MeetingRecordingRow, 'source' | 'copies'>,
): string | null {
  const sources = callSources(call)
  if (sources.length < 2) return null
  const names = sources.map((source) => SOURCE_CONFIG[source].label)
  return `Applies to the ${names.join(' and ')} recordings of this call.`
}

/**
 * Whether the open sheet is this call: its meeting id is the row's or any of the
 * row's copies' (a link made before the two recordings were shown as one call
 * still opens the right row).
 */
export function isSelectedCall(
  call: Pick<MeetingRecordingRow, 'id' | 'copies'>,
  selectedId: string | undefined,
): boolean {
  if (selectedId === undefined) return false
  return (
    call.id === selectedId || call.copies.some((copy) => copy.id === selectedId)
  )
}

/**
 * Whether the call was tied to a client: green when a client was found, amber
 * when none was (a prospect or an internal call), quiet while the matcher has
 * not looked yet. Every call keeps its transcript either way. These words are
 * the page's vocabulary for the three states, so every badge, filter option and
 * count reads them from here.
 */
export const LINK_STATUS_CONFIG: Record<MeetingLinkStatus, StatusBadgeConfig> =
  {
    MATCHED: { label: 'Client found', className: TONE.success },
    UNMATCHED: { label: 'No client', className: TONE.warning },
    PENDING: { label: 'Not checked yet', className: TONE.neutral },
  }

/**
 * Who attached a recording's HubSpot meeting. Green is the matcher alone, on a
 * key that names the exact booking (AUTO); blue is a person's choice
 * (CONFIRMED); the two "no meeting" states are quiet. A call that has a client
 * but no meeting is the one that waits for a person: see
 * {@link NEEDS_MEETING_CONFIG}.
 */
export const HUBSPOT_LINK_CONFIG: Record<
  MeetingHubspotLinkState,
  StatusBadgeConfig
> = {
  NONE: { label: 'No meeting', className: TONE.neutral },
  AUTO: { label: 'Linked automatically', className: TONE.success },
  CONFIRMED: { label: 'Attached by hand', className: TONE.info },
  DISMISSED: { label: 'Not a booked meeting', className: TONE.neutral },
}

/**
 * The three HubSpot-meeting figures of a source's card. They do not overlap, so
 * together they are the source's calls: every call has exactly one link state,
 * and the calls that need a meeting (client known, no meeting) are a part of
 * NONE that the first figure already counts.
 *
 * - `needsMeeting`: a client is known and no HubSpot meeting is attached.
 * - `linked`: a meeting is attached, by a key (AUTO) or by a person (CONFIRMED).
 * - `noMeeting`: neither is attached and nothing waits for a person: no client
 *   and no meeting (NONE less the calls that need a meeting), plus the calls a
 *   person marked as not a booked meeting (DISMISSED).
 */
export function hubspotMeetingFigures(
  sync: Pick<MeetingSourceSyncState, 'hubspotLinks' | 'needsMeeting'>,
): { needsMeeting: number; linked: number; noMeeting: number } {
  const { NONE, AUTO, CONFIRMED, DISMISSED } = sync.hubspotLinks
  return {
    needsMeeting: sync.needsMeeting,
    linked: AUTO + CONFIRMED,
    noMeeting: Math.max(0, NONE - sync.needsMeeting) + DISMISSED,
  }
}

/** The hint on a call whose client is known but that has no HubSpot meeting: amber, a person has something to do. */
export const NEEDS_MEETING_CONFIG: StatusBadgeConfig = {
  label: 'Needs a meeting',
  className: TONE.warning,
}

/**
 * Whether a call is waiting for a person to attach a HubSpot meeting: its
 * client is known and nothing is attached. That is the queue the Overview counts
 * and the Meetings filter lists; a call with no client (a prospect, say) is not
 * in it, though its attach dialog still suggests meetings, and a dismissal is
 * already an answer.
 */
export function needsHubspotMeeting(
  meeting: Pick<MeetingRecordingRow, 'client' | 'hubspotLink'>,
): boolean {
  return meeting.client !== null && meeting.hubspotLink === 'NONE'
}

/** The HubSpot-meeting filter's link-state options, in the order a person works through them. */
export const HUBSPOT_LINK_FILTER_OPTIONS: ReadonlyArray<{
  value: MeetingHubspotLinkState
  label: string
}> = (['AUTO', 'CONFIRMED', 'NONE', 'DISMISSED'] as const).map((value) => ({
  value,
  label: HUBSPOT_LINK_CONFIG[value].label,
}))

/**
 * What a call was for, from its attached HubSpot meeting's type as classified on
 * the meeting-types page. Sales and Service are the two that matter and get a
 * hue each (blue, green); the two "not known" states stay quiet, the missing
 * meeting with a dashed border so the two read apart.
 */
export const CATEGORY_CONFIG: Record<MeetingCallCategory, StatusBadgeConfig> = {
  SALES: { label: 'Sales', className: TONE.info },
  SERVICE: { label: 'Service', className: TONE.success },
  UNCLASSIFIED: { label: 'Not classified', className: TONE.neutral },
  NO_MEETING: {
    label: 'No meeting',
    className: `${TONE.neutral} border-dashed`,
  },
}

/** The Category filter's options, in the order the page lists the categories. */
export const CATEGORY_FILTER_OPTIONS: ReadonlyArray<{
  value: MeetingCallCategory
  label: string
}> = MEETING_CALL_CATEGORIES.map((value) => ({
  value,
  label: CATEGORY_CONFIG[value].label,
}))

/** The page's long names of the categories (the Overview's rows read them in full). */
export const CATEGORY_LONG_LABEL: Record<MeetingCallCategory, string> = {
  SALES: 'Sales',
  SERVICE: 'Service',
  UNCLASSIFIED: 'Not classified',
  NO_MEETING: 'No HubSpot meeting',
}

/**
 * HubSpot's own outcome of a meeting (hs_meeting_outcome) as a badge. Completed
 * is green and a no-show amber; every other state is quiet.
 */
const OUTCOME_CONFIG: Partial<Record<string, StatusBadgeConfig>> = {
  COMPLETED: { label: 'Completed', className: TONE.success },
  NO_SHOW: { label: 'No show', className: TONE.warning },
  CANCELED: { label: 'Canceled', className: TONE.neutral },
  RESCHEDULED: { label: 'Rescheduled', className: TONE.neutral },
  SCHEDULED: { label: 'Scheduled', className: TONE.neutral },
  DECLINED: { label: 'Declined', className: TONE.neutral },
}

/**
 * The badge for a HubSpot meeting outcome, or `null` when none is recorded (the
 * column shows a dash). The API sends HubSpot's own value (`NO_SHOW`); a state
 * this page has no word for is shown in sentence case, quietly, instead of
 * being hidden.
 */
export function meetingOutcomeBadge(
  outcome: string | null | undefined,
): StatusBadgeConfig | null {
  const raw = outcome?.trim()
  if (!raw) return null
  const key = raw.toUpperCase().replace(/[\s-]+/g, '_')
  const known = OUTCOME_CONFIG[key === 'CANCELLED' ? 'CANCELED' : key]
  if (known) return known
  const words = raw.replace(/[_-]+/g, ' ').toLowerCase()
  return {
    label: words.charAt(0).toUpperCase() + words.slice(1),
    className: TONE.neutral,
  }
}

export const TRANSCRIPT_STATUS_CONFIG: Record<
  MeetingTranscriptStatus,
  StatusBadgeConfig
> = {
  READY: { label: 'Ready', className: TONE.success },
  FAILED: { label: 'Failed', className: TONE.danger },
  PENDING: { label: 'Pending', className: TONE.info },
}

export const BATCH_STATUS_CONFIG: Record<
  MeetingSyncBatchStatus,
  StatusBadgeConfig
> = {
  PENDING: { label: 'Pending', className: TONE.warning },
  RUNNING: { label: 'Running', className: TONE.info },
  COMPLETED: { label: 'Completed', className: TONE.success },
  FAILED: { label: 'Failed', className: TONE.danger },
}

/** A backfill that has not finished: what the page polls and what blocks a new one. */
export function isBatchInFlight(status: MeetingSyncBatchStatus): boolean {
  return status === 'PENDING' || status === 'RUNNING'
}

/**
 * Whether a source can bring meetings in right now. Both run as soon as their
 * credentials are set: Zoom's three S2S credentials, Avoma's API key (Avoma is
 * backfill only, so the key is all it takes).
 */
export interface SourceReadiness {
  ready: boolean
  /** Short badge text: "Ready" or "Not configured". */
  label: string
}

export function zoomReadiness(
  status: Pick<ZoomSourceStatus, 'configured'>,
): SourceReadiness {
  return status.configured
    ? { ready: true, label: 'Ready' }
    : { ready: false, label: 'Not configured' }
}

export function avomaReadiness(
  status: Pick<AvomaSourceStatus, 'configured'>,
): SourceReadiness {
  return status.configured
    ? { ready: true, label: 'Ready' }
    : { ready: false, label: 'Not configured' }
}

/**
 * The backfill still PENDING or RUNNING for one source, if any. Each source
 * runs its own: a Zoom backfill never blocks an Avoma one, so the page asks
 * per source and not "is anything running".
 */
export function runningBackfillOf(
  status: Pick<MeetingTranscriptsStatus, 'zoom' | 'avoma'> | undefined,
  source: MeetingTranscriptSource,
): MeetingSyncBatchView | null {
  if (!status) return null
  return (source === 'ZOOM' ? status.zoom : status.avoma).runningBackfill
}

/**
 * The status the Integrations hub card shows. "Connected" means at least one
 * source can actually bring meetings in; a loaded status where none can is
 * "disconnected"; no data (loading, error, no permission) is "unknown" rather
 * than a guess.
 */
export type MeetingTranscriptsCardStatus =
  | 'connected'
  | 'disconnected'
  | 'unknown'

export function meetingTranscriptsCardStatus(
  status:
    | {
        zoom: Pick<ZoomSourceStatus, 'configured'>
        avoma: Pick<AvomaSourceStatus, 'configured'>
      }
    | undefined,
): MeetingTranscriptsCardStatus {
  if (!status) return 'unknown'
  return zoomReadiness(status.zoom).ready || avomaReadiness(status.avoma).ready
    ? 'connected'
    : 'disconnected'
}

/**
 * How the per-row "Replay" button renders. The API refuses only a delivery a
 * live worker is on (409, whose message the toast shows), so unlike the Chili
 * Piper log nothing is disabled up front: a PENDING one may be stuck (the
 * switch was off) and a PROCESSING one may be stale, which are exactly the
 * cases a replay is for. FAILED, IGNORED and PENDING get the plain outline
 * button; PROCESSED and PROCESSING are the exceptions and get a quieter one.
 */
export type ZoomReplayEmphasis = 'primary' | 'secondary'

export function zoomReplayEmphasis(
  status: ZoomWebhookEventStatus,
): ZoomReplayEmphasis {
  switch (status) {
    case 'FAILED':
    case 'IGNORED':
    case 'PENDING':
      return 'primary'
    case 'PROCESSED':
    case 'PROCESSING':
      return 'secondary'
  }
}

/**
 * A millisecond offset into a recording as `m:ss`, or `h:mm:ss` from one hour
 * on. Fractions of a second are dropped (not rounded) so a cue never reads
 * later than it starts; negatives and non-finite values read as `0:00`.
 */
export function formatOffset(ms: number): string {
  const totalSeconds = Number.isFinite(ms)
    ? Math.max(0, Math.floor(ms / 1000))
    : 0
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const ss = String(seconds).padStart(2, '0')
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${ss}`
  return `${minutes}:${ss}`
}

/** A meeting length from the minutes the source reports: `45 min`, `1 h`, `1 h 15 min`, or a dash. */
export function formatMeetingDuration(minutes: number | null): string {
  if (minutes === null || !Number.isFinite(minutes) || minutes <= 0) return '—'
  const whole = Math.round(minutes)
  const hours = Math.floor(whole / 60)
  const rest = whole % 60
  if (hours === 0) return `${whole} min`
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`
}

/**
 * The Transcript column of the meetings table. Only non-zero parts are named,
 * in the order an admin cares about; a meeting with no transcript is a dash.
 *
 *   { total: 1, ready: 1, ... }              -> "1 ready"
 *   { total: 2, ready: 1, failed: 1, ... }   -> "1 ready, 1 failed"
 *   { total: 0, ... }                        -> "—"
 */
export function transcriptSummaryLabel(
  transcripts: MeetingTranscriptCounts,
): string {
  const parts: Array<string> = []
  if (transcripts.ready > 0) parts.push(`${transcripts.ready} ready`)
  if (transcripts.failed > 0) parts.push(`${transcripts.failed} failed`)
  if (transcripts.pending > 0) parts.push(`${transcripts.pending} pending`)
  return parts.length > 0 ? parts.join(', ') : '—'
}

/**
 * A backfill's day range as one phrase. The API sends bare `YYYY-MM-DD` days,
 * which `formatDateOnlyShort` keeps on the calendar day they name; a one-day
 * range reads as that day alone.
 *
 *   formatDayRange('2026-03-01', '2026-03-05') -> "Mar 1, 2026 - Mar 5, 2026"
 *   formatDayRange('2026-03-01', '2026-03-01') -> "Mar 1, 2026"
 */
export function formatDayRange(from: string, to: string): string {
  if (from === to) return formatDateOnlyShort(from)
  return `${formatDateOnlyShort(from)} - ${formatDateOnlyShort(to)}`
}

/**
 * What an Avoma backfill says while it looks up calendar ids, before it lists:
 * `Matching calendar IDs (Chili Piper) · 12 of 40`. Without the count (the API
 * sends none) just what it is doing.
 */
export function calendarIdsPhaseLabel(
  progress: { done: number; total: number } | null,
): string {
  const label = 'Matching calendar IDs (Chili Piper)'
  return progress ? `${label} · ${progress.done} of ${progress.total}` : label
}

/** How long a batch ran: a dash until it has finished (or when its clock is off). */
export function batchDurationLabel(
  startedAtIso: string,
  completedAtIso: string | null,
): string {
  if (!completedAtIso) return '—'
  const ms = Date.parse(completedAtIso) - Date.parse(startedAtIso)
  return Number.isFinite(ms) ? formatDurationMs(ms) : '—'
}

/** The pickers hand back `yyyy-MM-ddT12:00:00.000Z`; the same shape keeps the defaults in step. */
function pickerInstant(date: Date): string {
  return `${format(date, 'yyyy-MM-dd')}T12:00:00.000Z`
}

/**
 * The range a backfill form opens with, in the date pickers' instants. Zoom
 * keeps about 90 days of recordings, so its default is the last 30; Avoma
 * keeps about two years, so its default reaches back that far.
 */
export function defaultBackfillRange(
  source: MeetingTranscriptSource,
  today: Date = new Date(),
): { from: string; to: string } {
  const from = source === 'AVOMA' ? subYears(today, 2) : subDays(today, 30)
  return { from: pickerInstant(from), to: pickerInstant(today) }
}

/**
 * The source the Backfill form starts on. Zoom unless the link names Avoma, and
 * Avoma only while its key is set: a link to an Avoma backfill that cannot run
 * lands on Zoom rather than on a form that can never be submitted.
 * `avomaConfigured` is `undefined` while the status has not loaded, which does
 * not overrule the link.
 */
export function effectiveBackfillSource(
  requested: MeetingTranscriptSource | undefined,
  avomaConfigured: boolean | undefined,
): MeetingTranscriptSource {
  if (requested === 'AVOMA' && avomaConfigured === false) return 'ZOOM'
  return requested ?? 'ZOOM'
}

/**
 * A fingerprint of everything a "Retry link + fetch" can change on a meeting:
 * how it is linked to a client, how its HubSpot meeting link stands, how many
 * times linking was tried, and each transcript file's id and state. After the retry is queued the detail is re-read until
 * this differs from the one taken before, which is how the page knows the
 * worker has finished, whatever the outcome.
 */
export function meetingChangeSignature(
  meeting: Pick<
    MeetingRecordingDetail,
    'linkStatus' | 'hubspotLink' | 'linkAttempts'
  > & {
    transcriptFiles: ReadonlyArray<
      Pick<MeetingTranscriptSummary, 'id' | 'status' | 'cueCount'>
    >
  },
): string {
  const files = meeting.transcriptFiles
    .map((file) => `${file.id}:${file.status}:${file.cueCount}`)
    .join(',')
  return `${meeting.linkStatus}|${meeting.hubspotLink}|${meeting.linkAttempts}|${files}`
}

/**
 * Whether the answer to a HubSpot link review means a transcript fetch was
 * queued. A review applies to the whole call, and every call keeps its
 * transcript, client or not, so the API fetches the transcript of each recording
 * of the call that has none. A call whose recordings all have one has nothing
 * coming. When the source cannot run the fetch (Zoom sync off, no Avoma key)
 * nothing is queued either; the caller watching for it just gives up at its
 * deadline.
 */
export function reviewQueuedTranscriptFetch(
  reviewed: Pick<MeetingRecordingDetail, 'transcripts' | 'copies'>,
): boolean {
  return (
    reviewed.transcripts.total === 0 ||
    reviewed.copies.some((copy) => copy.transcripts.total === 0)
  )
}

/**
 * A call's transcript files in the order the sheet lists them: Avoma's first,
 * because it tells staff from client speakers, then the rest as the API sent
 * them (stable).
 */
export function orderTranscriptFiles<
  T extends Pick<MeetingTranscriptSummary, 'source'>,
>(files: ReadonlyArray<T>): Array<T> {
  const rank = (file: T) => (file.source === 'AVOMA' ? 0 : 1)
  return files
    .map((file, index) => ({ file, index }))
    .sort((a, b) => rank(a.file) - rank(b.file) || a.index - b.index)
    .map(({ file }) => file)
}

/**
 * The transcript "View transcript" leads with: the first ready one in the
 * sheet's order, so Avoma's when both sources have one. `undefined` when none is
 * ready.
 */
export function defaultTranscriptFile<
  T extends Pick<MeetingTranscriptSummary, 'source' | 'status'>,
>(files: ReadonlyArray<T>): T | undefined {
  return orderTranscriptFiles(files).find((file) => file.status === 'READY')
}

/** The call's HubSpot contacts as the sheet shows them: the main one, then the others. */
export function splitHubspotContacts(
  contacts: ReadonlyArray<MeetingHubspotContactRef>,
): {
  main: MeetingHubspotContactRef | null
  others: Array<MeetingHubspotContactRef>
} {
  const main =
    contacts.find((contact) => contact.isMain) ?? contacts.at(0) ?? null
  return {
    main,
    others: contacts.filter((contact) => contact !== main),
  }
}

/** One uninterrupted stretch of a speaker talking, as the viewer shows it. */
export interface TranscriptTurn {
  /** The first cue's ordinal: stable across renders, so a usable React key. */
  id: number
  speaker: string | null
  /** The speaker's email, when the source gave it (Avoma). */
  speakerEmail: string | null
  /** True for Q3 staff, false for the client, null when the source does not say. */
  speakerIsStaff: boolean | null
  /** Offset of the turn's first cue. */
  startMs: number
  /** End of the turn's last cue. */
  endMs: number
  /** The cues' text joined with a space. */
  text: string
}

/**
 * Folds consecutive cues by the same speaker into one turn.
 *
 * Two cues join only when the same name also carries the same staff/client
 * flag, so two people who share a display name stay apart. A cue with no
 * speaker is never merged, neither with a named speaker nor
 * with another unnamed cue: a transcript without speaker labels would
 * otherwise collapse into one wall of text with a single timestamp, where one
 * turn per cue keeps the timing. Cues are taken in the order given (the API
 * sorts them by ordinal).
 */
export function groupCuesIntoTurns(
  cues: ReadonlyArray<MeetingTranscriptCueView>,
): Array<TranscriptTurn> {
  const turns: Array<TranscriptTurn> = []
  for (const cue of cues) {
    const last = turns.at(-1)
    if (
      last &&
      cue.speaker !== null &&
      last.speaker === cue.speaker &&
      last.speakerIsStaff === cue.speakerIsStaff
    ) {
      last.text = `${last.text} ${cue.text}`
      last.endMs = cue.endMs
      continue
    }
    turns.push({
      id: cue.ordinal,
      speaker: cue.speaker,
      speakerEmail: cue.speakerEmail,
      speakerIsStaff: cue.speakerIsStaff,
      startMs: cue.startMs,
      endMs: cue.endMs,
      text: cue.text,
    })
  }
  return turns
}

/**
 * What the transcript says a speaker is, by name: staff, client, or `null` when
 * the source does not say (Zoom) or the same name appears with both flags. A
 * name that never speaks is absent.
 */
export function speakerRolesByName(
  cues: ReadonlyArray<MeetingTranscriptCueView>,
): Map<string, boolean | null> {
  const roles = new Map<string, boolean | null>()
  for (const cue of cues) {
    if (cue.speaker === null) continue
    const known = roles.get(cue.speaker)
    if (known === undefined) roles.set(cue.speaker, cue.speakerIsStaff)
    else if (known !== cue.speakerIsStaff) roles.set(cue.speaker, null)
  }
  return roles
}

/** "Staff" or "Client", or `null` when the source does not say. */
export function speakerRoleLabel(isStaff: boolean | null): string | null {
  if (isStaff === null) return null
  return isStaff ? 'Staff' : 'Client'
}

/** A run of text, and whether it is a hit for the search query. */
export interface HighlightPart {
  text: string
  match: boolean
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Splits `text` around every case-insensitive occurrence of `query`, for
 * rendering hits in a `<mark>`. The query is taken literally (never a
 * pattern) and trimmed; a blank query yields the text as one non-match part.
 * Joining the parts always gives back `text`.
 */
export function searchHighlightParts(
  text: string,
  query: string,
): Array<HighlightPart> {
  const needle = query.trim()
  if (!needle || !text) return [{ text, match: false }]
  const pattern = new RegExp(`(${escapeRegExp(needle)})`, 'gi')
  const parts: Array<HighlightPart> = []
  let cursor = 0
  for (const hit of text.matchAll(pattern)) {
    if (hit.index > cursor) {
      parts.push({ text: text.slice(cursor, hit.index), match: false })
    }
    parts.push({ text: hit[0], match: true })
    cursor = hit.index + hit[0].length
  }
  if (cursor < text.length) {
    parts.push({ text: text.slice(cursor), match: false })
  }
  return parts
}

/** How many times `query` occurs in `text`, case-insensitively; 0 for a blank query. */
export function countMatches(text: string, query: string): number {
  return searchHighlightParts(text, query).filter((part) => part.match).length
}

/** "No speakers", "1 speaker" or "3 speakers". */
export function speakerCountLabel(count: number): string {
  if (count <= 0) return 'No speakers'
  return count === 1 ? '1 speaker' : `${count} speakers`
}

/** The label for a recording's external id: each source calls it something else. */
export function externalIdLabel(source: MeetingTranscriptSource): string {
  return `${SOURCE_CONFIG[source].label} meeting UUID`
}

/* ---- Meetings tab in the URL ----
 * The tab's filters and page travel in the route's search params so a deep
 * link, a reload and the Back button all land on the same list. */

/**
 * The link-status filter as read from `?linkStatus=MATCHED,UNMATCHED`: the
 * comma-separated codec every multi-select in the app uses, minus anything
 * that is not a status (a stale or hand-edited link must not send the API a
 * value it answers 400 to).
 */
export function parseLinkStatusParam(
  raw: string | undefined | null,
): Array<MeetingLinkStatus> {
  const valid: ReadonlyArray<string> = MEETING_LINK_STATUSES
  return parseCsvParam(raw).filter((value): value is MeetingLinkStatus =>
    valid.includes(value),
  )
}

/** The inverse of {@link parseLinkStatusParam}; none selected drops the param. */
export function serializeLinkStatusParam(
  statuses: ReadonlyArray<MeetingLinkStatus>,
): string | undefined {
  return serializeCsvParam(statuses)
}

/**
 * The category filter as read from `?category=SALES,SERVICE`: the same
 * comma-separated codec, minus anything that is not a category.
 */
export function parseCategoryParam(
  raw: string | undefined | null,
): Array<MeetingCallCategory> {
  const valid: ReadonlyArray<string> = MEETING_CALL_CATEGORIES
  return parseCsvParam(raw).filter((value): value is MeetingCallCategory =>
    valid.includes(value),
  )
}

/** The inverse of {@link parseCategoryParam}; none selected drops the param. */
export function serializeCategoryParam(
  categories: ReadonlyArray<MeetingCallCategory>,
): string | undefined {
  return serializeCsvParam(categories)
}

/** `?transcriptStatus=` as the filter it names, or `undefined` for anything else. */
export function parseTranscriptFilterParam(
  raw: string | undefined | null,
): (typeof MEETING_TRANSCRIPT_FILTERS)[number] | undefined {
  return MEETING_TRANSCRIPT_FILTERS.find((value) => value === raw)
}

/** `?hubspotLink=` as the state it names, or `undefined` for anything else. */
export function parseHubspotLinkParam(
  raw: string | undefined | null,
): MeetingHubspotLinkState | undefined {
  return MEETING_HUBSPOT_LINK_STATES.find((value) => value === raw)
}

/**
 * One option of the meeting-type filter as the dropdown words it, with how
 * many recordings carry it: `Escape Plan Meeting · 12`.
 */
export function meetingTypeOptionLabel(option: MeetingTypeOption): string {
  return `${option.value} · ${option.count}`
}

/**
 * The meeting-type dropdown's items: the API's options, most used first, plus
 * the type the URL names when the options do not list it (a link made when it
 * was in use, or the options not loaded yet), so the dropdown never shows a
 * choice it cannot name. The kept type has no count to show.
 */
export function meetingTypeSelectItems(
  options: ReadonlyArray<MeetingTypeOption>,
  current: string | undefined,
): Array<{ value: string; label: string }> {
  const items = options.map((option) => ({
    value: option.value,
    label: meetingTypeOptionLabel(option),
  }))
  if (current !== undefined && !options.some((o) => o.value === current)) {
    items.push({ value: current, label: current })
  }
  return items
}

/** The shape of a day in the URL and in the API: `YYYY-MM-DD`. */
export const DAY_PARAM_PATTERN = /^\d{4}-\d{2}-\d{2}$/

/**
 * A day from the URL as the instant the date pickers speak, which is noon UTC
 * of that day (`DatePickerField` reads UTC components, so noon survives any
 * time zone). Anything that is not a real calendar day is `null`.
 */
export function dayToPickerInstant(
  day: string | undefined | null,
): string | null {
  if (!day || !DAY_PARAM_PATTERN.test(day)) return null
  const instant = `${day}T12:00:00.000Z`
  // The round trip rejects a day the calendar lacks (Feb 30 rolls into March).
  const date = new Date(instant)
  return !Number.isNaN(date.getTime()) && date.toISOString() === instant
    ? instant
    : null
}

/** The `YYYY-MM-DD` a date picker's instant names, or `undefined` for none. */
export function pickerInstantToDay(iso: string | null): string | undefined {
  return iso ? iso.slice(0, 10) : undefined
}

export const EMPTY_MEETINGS_FILTERS: MeetingsFilterState = {
  source: undefined,
  category: [],
  linkStatus: [],
  hubspotLink: undefined,
  needsMeeting: false,
  meetingType: undefined,
  transcriptStatus: undefined,
  from: null,
  to: null,
  search: '',
  tag: [],
  q: '',
}

/** Whether any Meetings filter is on, which changes what an empty list means. */
export function hasActiveMeetingsFilters(
  filters: MeetingsFilterState,
): boolean {
  return (
    filters.source !== undefined ||
    filters.category.length > 0 ||
    filters.linkStatus.length > 0 ||
    filters.hubspotLink !== undefined ||
    filters.needsMeeting ||
    filters.meetingType !== undefined ||
    filters.transcriptStatus !== undefined ||
    filters.from !== null ||
    filters.to !== null ||
    filters.search.trim() !== '' ||
    filters.tag.length > 0 ||
    filters.q.trim() !== ''
  )
}

/* ---- HubSpot meeting link: the words around attaching one ---- */

/** What ties a candidate HubSpot meeting to the call, as a chip in the picker. */
export const LINK_EVIDENCE_LABEL: Record<MeetingLinkEvidenceKind, string> = {
  CALENDAR: 'Same calendar event',
  AVOMA: 'Avoma link in HubSpot notes',
  ZOOM: 'Same Zoom meeting',
  CLIENT: "Client's meeting",
}

/**
 * Why the picker highlights a candidate, as a chip. A suggestion is a pointer for
 * the person choosing; nothing is attached or preselected by it.
 */
export const SUGGESTION_REASON_LABEL: Record<MeetingSuggestionReason, string> =
  {
    ZOOM_COPY: "Zoom recording's meeting",
    HOST_MEETING: "Host's meeting",
    SAME_START: 'Same start',
    SAME_TITLE: 'Same title',
    SAME_LENGTH: 'Same length',
  }

/** The "Suggested" tag and the tint of a candidate the API suggests: blue, apart from the primary tint of the one picked. */
export const SUGGESTED_CONFIG: StatusBadgeConfig = {
  label: 'Suggested',
  className: TONE.info,
}

/** The highlight of a suggested candidate's row (a tint and a ring, never a selection). */
export const SUGGESTED_ROW_CLASS = 'border-blue-500/40 bg-blue-500/5'

/** Whether the API suggests this candidate (it names at least one reason). */
export function isSuggestedCandidate(
  candidate: Pick<MeetingHubspotCandidate, 'suggestedBecause'>,
): boolean {
  return candidate.suggestedBecause.length > 0
}

/** The keys that attach a meeting by themselves; the picker puts these first and marks them with a chip. */
export function isKeyedEvidence(kind: MeetingLinkEvidenceKind): boolean {
  return kind === 'CALENDAR' || kind === 'AVOMA' || kind === 'ZOOM'
}

/** A gap between two start times, in the largest whole unit that reads well: `45 min`, `3 h`, `68 days`. */
function gapLabel(minutes: number): string {
  if (minutes < 24 * 60) return formatMeetingDuration(minutes)
  const days = Math.round(minutes / (24 * 60))
  return days === 1 ? '1 day' : `${days} days`
}

/**
 * Where a candidate's booked start is against the call, for the picker: the
 * API's `deltaMinutes` is the size of the gap only, so the direction is read
 * from the two start times. Without both it says how far apart they are with a
 * "±" and nothing more.
 *
 *   "3 h after the call", "68 days before the call", "Same time as the call"
 */
export function candidateTimingLabel(timing: {
  deltaMinutes: number
  callStartIso: string
  bookedStartIso: string | null
}): string {
  if (timing.deltaMinutes < 1) return 'Same time as the call'
  const gap = gapLabel(timing.deltaMinutes)
  const call = Date.parse(timing.callStartIso)
  const booked = timing.bookedStartIso ? Date.parse(timing.bookedStartIso) : NaN
  if (!Number.isFinite(call) || !Number.isFinite(booked) || call === booked) {
    return `±${gap} from the call`
  }
  return booked > call ? `${gap} after the call` : `${gap} before the call`
}

/**
 * The picker's search box: the candidates whose title, meeting type or date
 * contain every word typed, in any order and any case. The date is searched as
 * the list shows it ("Oct 19, 2026, 3:10 PM") and as `YYYY-MM-DD`; a meeting
 * without a title is found as "Untitled meeting". A word that is only digits
 * ("20", "2026") must be a whole number in the text, so "Oct 20" finds the 20th
 * and not every meeting of 2026. A blank query keeps them all.
 */
export function filterHubspotCandidates<
  T extends Pick<
    MeetingHubspotCandidate,
    'title' | 'meetingType' | 'startTimeIso'
  >,
>(items: ReadonlyArray<T>, query: string): Array<T> {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (terms.length === 0) return [...items]
  return items.filter((item) => {
    const haystack = [
      item.title?.trim() || 'Untitled meeting',
      item.meetingType ?? '',
      item.startTimeIso ? formatDateTime(item.startTimeIso) : '',
      item.startTimeIso ? item.startTimeIso.slice(0, 10) : '',
    ]
      .join(' ')
      .toLowerCase()
    const words = haystack.split(/[^a-z0-9-]+/)
    return terms.every((term) =>
      /^\d+$/.test(term) ? words.includes(term) : haystack.includes(term),
    )
  })
}

/**
 * The client to send with a candidate the person attaches: the client shown in
 * the picker, when the candidate is one of that client's meetings (the API
 * answers 400 for any other). A meeting that was found only by the call's
 * calendar event or Zoom number, and belongs to someone else, is sent without
 * one, and the API takes the client from the meeting.
 */
export function attachClientId(
  candidate: Pick<MeetingHubspotCandidate, 'matchedBy' | 'clients'>,
  shownClientId: string | null,
): string | undefined {
  if (shownClientId === null) return undefined
  const theirs =
    candidate.matchedBy.includes('CLIENT') ||
    candidate.clients.some((client) => client.id === shownClientId)
  return theirs ? shownClientId : undefined
}

/**
 * The line under an attached or dismissed HubSpot link: how an automatic one
 * was found, or who decided and when. `null` for NONE, which has nothing to say.
 * The reviewer can be gone (the user was deleted), in which case only the date
 * is named.
 *
 *   "How it was found: Same calendar event"
 *   "Attached by Ana Lee · Oct 20, 2026"
 *   "Marked not a booked meeting by Ana Lee · Oct 20, 2026"
 */
export function hubspotReviewNote(
  review: Pick<
    MeetingHubspotLinkReview,
    'state' | 'matchedBy' | 'reviewedBy' | 'reviewedAtIso'
  >,
): string | null {
  const by = review.reviewedBy
    ? ` by ${review.reviewedBy.name ?? review.reviewedBy.email}`
    : ''
  const on = review.reviewedAtIso ? formatDate(review.reviewedAtIso) : null
  const dated = (text: string) => (on ? `${text} · ${on}` : text)
  switch (review.state) {
    case 'AUTO':
      return review.matchedBy
        ? `How it was found: ${LINK_EVIDENCE_LABEL[review.matchedBy]}`
        : 'Linked automatically'
    case 'CONFIRMED':
      return dated(`Attached${by}`)
    case 'DISMISSED':
      return dated(`Marked not a booked meeting${by}`)
    case 'NONE':
      return null
  }
}
